import React, { useState } from 'react';

/* ─────────────────────────────────────────────────────────
 * CODE BLOCK & SANDBOX RUNNER
 * Agent-written code streaming with line numbers, copy,
 * and live execution in browser sandbox.
 * ───────────────────────────────────────────────────────── */

export default function CodeBlock({
  lang = "typescript",
  code = `// Leak-free Reactive Scroll Hook\nimport { useEffect, useState } from 'react';\n\nexport function useOptimizedScroll(threshold = 50) {\n  const [isScrolled, setIsScrolled] = useState(false);\n\n  useEffect(() => {\n    const controller = new AbortController();\n    let ticking = false;\n\n    const onScroll = () => {\n      if (!ticking) {\n        window.requestAnimationFrame(() => {\n          setIsScrolled(window.scrollY > threshold);\n          ticking = false;\n        });\n        ticking = true;\n      }\n    };\n\n    window.addEventListener('scroll', onScroll, {\n      passive: true,\n      signal: controller.signal\n    });\n\n    return () => controller.abort();\n  }, [threshold]);\n\n  return isScrolled;\n}`,
  isExecutable = true,
}: {
  lang?: string;
  code?: string;
  isExecutable?: boolean;
}) {
  const [copied, setCopied] = useState(false);
  const [runnerOpen, setRunnerOpen] = useState(false);
  const lines = code.trim().split('\n');

  const copy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="w-full max-w-105 rounded-card bg-[#0b0d13] border border-line shadow-card overflow-hidden my-2 font-mono">
      {/* Code Header */}
      <div className="flex items-center justify-between px-3 py-2 bg-[#121620] border-b border-line">
        <span className="text-[11px] font-semibold text-ink-3 uppercase">{lang}</span>
        <div className="flex items-center gap-1.5">
          {isExecutable && (
            <button
              onClick={() => setRunnerOpen(!runnerOpen)}
              className="flex items-center gap-1 px-2 py-1 rounded-[5px] text-[11px] font-medium bg-green-tint text-green hover:bg-green hover:text-white transition-colors"
            >
              <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
                <polygon points="5 3 19 12 5 21 5 3" />
              </svg>
              <span>{runnerOpen ? 'Close Runner' : 'Run Sandbox'}</span>
            </button>
          )}
          <button
            onClick={copy}
            className="flex items-center gap-1 px-2 py-1 rounded-[5px] text-[11px] text-ink-3 hover:text-ink hover:bg-hover transition-colors"
          >
            {copied ? '✓ Copied' : 'Copy'}
          </button>
        </div>
      </div>

      {/* Code Content */}
      <div className="flex overflow-x-auto text-[12px] leading-relaxed p-3 text-ink-2">
        <div className="select-none pr-3 text-ink-3/40 text-right border-r border-line/40">
          {lines.map((_, i) => (
            <div key={i}>{i + 1}</div>
          ))}
        </div>
        <pre className="pl-3.5 whitespace-pre">
          <code>{code}</code>
        </pre>
      </div>

      {/* Simulated Sandbox Execution Drawer */}
      {runnerOpen && (
        <div className="border-t border-line bg-black/50 p-3 text-[11px] text-green font-mono">
          <div className="flex justify-between text-ink-3 text-[10px] uppercase mb-1">
            <span>V8 In-Browser Sandbox</span>
            <span>Exit Code: 0</span>
          </div>
          <div>&gt; Compiling TypeScript AST...</div>
          <div>&gt; Attaching AbortSignal listener on window.scroll</div>
          <div>&gt; [SUCCESS] Event handler clean teardown verified. Peak heap 6.2MB.</div>
        </div>
      )}
    </div>
  );
}
